import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CONTACT } from "../../config/contact";

const links = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#industries", label: "Industries We Serve" },
  { href: "#workflow", label: "Workflow" },
  { href: "#clientele", label: "Clientele" },
  { href: "#contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div
          className={`flex items-center justify-between rounded-full px-5 sm:px-6 transition-all duration-300 ${
            scrolled
              ? "glass shadow-[0_10px_40px_-20px_rgba(11,31,58,0.25)] py-3"
              : "py-3 bg-white/40 backdrop-blur-md border border-transparent"
          }`}
        >
          <a
            data-testid="brand-logo"
            href="#top"
            aria-label="Libros Consulting — Home"
            className="flex items-center gap-3 group"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_accessibility-first-5/artifacts/4w169tlv_ChatGPT%20Image%20Jan%201%2C%202026%2C%2002_13_50%20AM.png"
              alt=""
              className={`rounded-full object-cover transition-all duration-300 ${scrolled ? "h-12 w-12" : "h-16 w-16"}`}
            />
            <span className="font-display text-[19px] font-semibold text-[#0B1F3A] tracking-tight hidden sm:inline">
              Libros Consulting
            </span>
          </a>

          <nav aria-label="Main navigation" className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                href={l.href}
                className="nav-underline px-4 py-2 text-[14px] font-medium text-[#0B1F3A]/75 hover:text-[#0B1F3A] rounded-full transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a
              data-testid="nav-cta-talk"
              href={CONTACT.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Schedule a Consultation (opens in new tab)"
              className="group inline-flex items-center gap-1.5 rounded-full bg-[#0B1F3A] text-white px-5 py-2.5 text-[14px] font-semibold hover:bg-[#1F5EFF] hover:shadow-[0_12px_30px_-12px_rgba(31,94,255,0.6)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
            >
              Schedule a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#0B1F3A]/5 text-[#0B1F3A]"
            onClick={() => setOpen((s) => !s)}
          >
            {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              data-testid="mobile-menu"
              id="mobile-menu"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="md:hidden mt-2 rounded-2xl glass p-3"
            >
              <div className="flex flex-col">
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="px-4 py-3 text-[15px] font-medium text-[#0B1F3A] rounded-xl hover:bg-[#0B1F3A]/5"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  href={CONTACT.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Schedule a Consultation (opens in new tab)"
                  onClick={() => setOpen(false)}
                  className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0B1F3A] text-white px-5 py-3 text-[14px] font-semibold"
                >
                  Schedule a Consultation <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
